// Libraries
import _ from 'lodash';

// Plugins
import { DASHBOARD_NULLISH_TEXT } from './general';

// Locale
import i18n from '../locale/i18n';

// Types
import {
  CrusherAutomationType,
  CrusherDataSchema,
  CrusherDriveMotorType,
  CrusherMetalDetectorStatus,
  CrusherModel,
  CrusherMotorControl,
  CrusherOperationalData,
  CrusherOperationMode,
  CrusherOverallStatus,
  CrusherPLCTagSchema,
  CrusherRunningEStatus,
  CrusherRunningSStatus,
  CrusherVSIDriveType,
  CrusherWeatherModeStatus,
  EquipmentDataReadStatus,
  EquipmentUnitCrusher,
  EquipmentUnitDataConeCrusher,
  EquipmentUnitDataVSICrusher,
  LubricationFlowStatus,
  SchemaType,
  SolenoidStatus,
} from '../types/crusher';
import {
  AlarmStatus,
  EdgeDeviceOperationalStatus,
  EdgeDeviceOperationalStatusStatus,
  EquipmentAlarmThresholds,
  GPS,
  Thresholds,
} from '../types/global';
import { Theme } from '../types/theme';

// Components
import { TagStatus } from '../components/EquipmentStatusTag';
import { EnableItemStatus } from '../components/EnableItem';
import { HightlightCardStatus } from '../components/HighlightCard';
import { ModuleIFMStatus } from '../components/ModuleIFM';
import { StatusIndicatorStatus } from '../layout/elements/StatusIndicator';
import { TagType } from '../layout/elements/Tag';
import { QuantityStatus } from '../layout/elements/QuantityCard';

/**
  The threshold to consider the DEVICE disconnected.
*/
export const DEVICE_COMMUNICATION_THRESHOLD: number = 1000 * 60 * 1;

/**
 * Checks if the edge device is considered disconnected based on the last data timestamp.
 *
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @param {number} threshold - The threshold to consider the Device disconnected.
 * @returns {boolean} - Returns true if the device is disconnected, false otherwise.
 */
export const isDeviceDisconnected = (
  lastDataTimestamp: number | null | undefined,
  threshold: number = DEVICE_COMMUNICATION_THRESHOLD,
): boolean => {
  let isDisconnected: boolean = false;

  if (!_.isNil(lastDataTimestamp)) {
    isDisconnected = (Date.now() - lastDataTimestamp) > threshold;
  }

  return isDisconnected;
};

/**
 * Converts the overall status of the crusher to a localized text string.
 *
 * @param {CrusherOverallStatus | null} [overallStatus] - The overall status of the crusher.
 * @returns {string} - The localized text string corresponding to the crusher's overall status.
 */
export const overallStatusToText = (
  overallStatus: CrusherOverallStatus | null | undefined,
): string => {
  switch (overallStatus) {
    case CrusherOverallStatus.good:
      return i18n.t('common.good');
    case CrusherOverallStatus.warning:
      return i18n.t('common.warning');
    case CrusherOverallStatus.alarm:
      return i18n.t('common.alarm.singular');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the overall status of the crusher to a TagType.
 *
 * @param {CrusherOverallStatus | null} [overallStatus] - The overall status of the crusher.
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * crusher's edge device.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType corresponding to the crusher's overall status.
 */
export const overallStatusToTagType = (
  overallStatus: CrusherOverallStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (overallStatus) {
    case CrusherOverallStatus.good:
      return TagType.success;
    case CrusherOverallStatus.warning:
      return TagType.warning;
    case CrusherOverallStatus.alarm:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the lubrication flow status to a localized text string.
 *
 * @param {LubricationFlowStatus | null} [lubricationFlowStatus] - The lubrication flow status.
 * @returns {string} - The localized text string corresponding to the lubrication flow status.
 */
export const lubricationFlowStatusToText = (
  lubricationFlowStatus: LubricationFlowStatus | null | undefined,
): string => {
  switch (lubricationFlowStatus) {
    case LubricationFlowStatus.good:
      return i18n.t('common.good');
    case LubricationFlowStatus.low:
      return i18n.t('common.low');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the lubrication flow status to a TagType.
 *
 * @param {LubricationFlowStatus | null} [lubricationFlowStatus] - The lubrication flow status.
 * @param {EdgeDeviceOperationalStatusStatus} operationalStatus - The operational status of the
 * crusher's edge device.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last data received.
 * @returns {TagType} - The TagType corresponding to the lubrication flow status.
 */
export const lubricationFlowStatusToTagType = (
  lubricationFlowStatus: LubricationFlowStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (lubricationFlowStatus) {
    case LubricationFlowStatus.good:
      return TagType.success;
    case LubricationFlowStatus.low:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the permissive status to a localized text string.
 *
 * @param {boolean | null} [permissiveStatus] - The permissive status.
 * @returns {string} - The localized text string corresponding to the permissive status.
 */
export const permissiveStatusToText = (permissiveStatus: boolean | null | undefined): string => {
  switch (permissiveStatus) {
    case true:
      return i18n.t('common.fault');
    case false:
      return i18n.t('common.good');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the permissive status to a localized text string (true = good, false = fault).
 *
 * @param {boolean | null} [permissiveStatus] - The permissive status.
 * @returns {string} - The localized text string corresponding to the permissive status.
 */
export const permissiveAlarmToStatusTag = (
  permissiveStatus: boolean | null | undefined,
): string => {
  switch (permissiveStatus) {
    case true:
      return i18n.t('common.good');
    case false:
      return i18n.t('common.fault');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the lid status to a localized text string (true = good, false = fault).
 *
 * @param {boolean | null} [status] - The lid status.
 * @returns {string} - The localized text string corresponding to the lid status.
 */
export const lidStatusToText = (status: boolean | null | undefined): string => {
  switch (status) {
    case false:
      return i18n.t('view.crusher.vsi.lidStatus.open');
    case true:
      return i18n.t('view.crusher.vsi.lidStatus.closed');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the lid status to a TagType.
 *
 * @param {boolean | null} [status] - The lid status.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status of
 * the edge device.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @returns {TagType} - The TagType corresponding to the lid status.
*/
export const lidStatusToTagType = (
  status: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (status) {
    case true:
      return TagType.success;
    case false:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the metal detector status to a TagType.
 *
 * @param {CrusherMetalDetectorStatus | null | undefined} status - The metal detector status.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status of
 * the edge device.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @returns {TagType} - The TagType corresponding to the metal detector status.
 */
export const metalDetectorStatusToTagType = (
  status: CrusherMetalDetectorStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (status) {
    case CrusherMetalDetectorStatus.clear:
      return TagType.success;
    case CrusherMetalDetectorStatus.detected:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the metal detector status to a localized text string.
 *
 * @param {CrusherMetalDetectorStatus | null | undefined} status - The metal detector status.
 * @returns {string} - The localized text string corresponding to the metal detector status.
 */
export const metalDetectorStatusToText = (
  status: CrusherMetalDetectorStatus | null | undefined,
): string => {
  switch (status) {
    case CrusherMetalDetectorStatus.clear:
      return i18n.t('view.crusher.vsi.metalDetector.clear');
    case CrusherMetalDetectorStatus.detected:
      return i18n.t('view.crusher.vsi.metalDetector.detected');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the edge device operational status and permissive status to a TagType.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * edge device.
 * @param {boolean | null} [permissiveStatus] - The permissive status.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType corresponding to the edge device operational status and
 * permissive status.
 */
export const permissiveStatusToTagType = (
  permissiveStatus: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (permissiveStatus) {
    case true:
      return TagType.critical;
    case false:
      return TagType.success;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the edge device operational status and permissive status to a TagType.
 * (true = success, false = critical).
 *
 * @param {boolean | null} permissiveStatus - The permissive status.
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * edge device.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType corresponding to the edge device operational status and
 * permissive status.
 */
export const booleanStatusToTagType = (
  permissiveStatus: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (permissiveStatus) {
    case true:
      return TagType.success;
    case false:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the edge device operational status and permissive status to a StatusIndicatorStatus.
 * (true = success, false = critical).
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * edge device.
 * @param {boolean | null} [permissiveStatus] - The permissive status.
 * @returns {StatusIndicatorStatus} - The StatusIndicatorStatus corresponding to the edge device
 * operational status and permissive status.
 */
export const permissiveStatusToAlarmTagType = (
  permissiveStatus: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    return TagType.neutral;
  }

  switch (permissiveStatus) {
    case true:
      return TagType.success;
    case false:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the edge device operational status and permissive status to a StatusIndicatorStatus.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * edge device.
 * @param {boolean | null} [permissiveStatus] - The permissive status.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {StatusIndicatorStatus} - The StatusIndicatorStatus corresponding to the edge device
 * operational status and permissive status.
 */
export const permissiveStatusToIndicatorStatus = (
  permissiveStatus: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  switch (permissiveStatus) {
    case true:
      return StatusIndicatorStatus.good;
    case false:
      return StatusIndicatorStatus.alarm;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Converts the edge device operational status and permissive status to a StatusIndicatorStatus
 * with an inverted logic (true = alarm, false = good).
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * edge device.
 * @param {boolean | null} [permissiveStatus] - The permissive status.
 * @returns {StatusIndicatorStatus} - The StatusIndicatorStatus corresponding to the edge device
 * operational status and permissive status.
 */
export const permissiveStatusToAlarmIndicatorStatus = (
  permissiveStatus: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    return StatusIndicatorStatus.neutral;
  }

  switch (permissiveStatus) {
    case true:
      return StatusIndicatorStatus.alarm;
    case false:
      return StatusIndicatorStatus.good;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Converts the discharge running status to a StatusIndicatorStatus.
 * (true = good, false = alarm).
 * @param {CrusherRunningSStatus | null | undefined} dischargeRunningStatus - The running status of
 * the discharge.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status of
 * the edge device.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @returns {StatusIndicatorStatus} - The StatusIndicatorStatus corresponding to the discharge
 * running status.
 */
export const dischargeRunningStatusToIndicatorStatus = (
  dischargeRunningStatus: CrusherRunningSStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  switch (dischargeRunningStatus) {
    case CrusherRunningSStatus.running:
      return StatusIndicatorStatus.good;
    case CrusherRunningSStatus.starting:
      return StatusIndicatorStatus.warning;
    case CrusherRunningSStatus.fault:
      return StatusIndicatorStatus.alarm;
    case CrusherRunningSStatus.off:
      return StatusIndicatorStatus.neutral;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Converts the crusher operation mode based on two fields: local/remote and service.
 *
 * @param {CrusherOperationMode | null | undefined} localRemote - Indicates if the mode is local or
 * remote.
 * @param {CrusherOperationMode | null | undefined} operateService -Indicates if the service mode
 * is active.
 * @returns {string} - The localized text string corresponding to the crusher's operation mode.
 */
export const operationModeToText = (
  localRemote: CrusherOperationMode | null | undefined,
  operateService: CrusherOperationMode | null | undefined,
): string => {
  if (!localRemote && !operateService) {
    return DASHBOARD_NULLISH_TEXT;
  }

  switch (true) {
    // Service mode
    case operateService === CrusherOperationMode.service:
      return i18n.t('common.service');

    // Local mode
    case localRemote === CrusherOperationMode.local
    && operateService !== CrusherOperationMode.service:
      return i18n.t('common.local');

    // Remote mode
    case localRemote === CrusherOperationMode.remote
    && operateService !== CrusherOperationMode.service:
      return i18n.t('common.remote');

    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the vibration sensor value to a localized text string based on the provided thresholds.
 *
 * @param {ThresholdsType} thresholds - The thresholds for the vibration sensor.
 * @param {number | null} [vibrationSensor] - The current value of the vibration sensor.
 * @returns {string} - The localized text string corresponding to the vibration sensor's status.
 */
export const vibrationSensorToText = (
  vibrationSensor: number | null,
  thresholds: EquipmentAlarmThresholds,
): string => {
  if (_.isNil(vibrationSensor)) {
    return DASHBOARD_NULLISH_TEXT;
  }

  if (!_.isNil(thresholds.highWarning) && vibrationSensor > thresholds.highWarning) {
    return i18n.t('common.warning');
  }

  return i18n.t('common.ok');
};

/**
 * Converts the edge device operational status, vibration sensor value, and data timestamp to
 *  a TagType.
 *
 * @param {number | null} vibrationSensor - The current value of the vibration sensor.
 * @param {EquipmentAlarmThresholds} thresholds - The thresholds for the vibration sensor.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status of
 * the edge device.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @returns {TagType} - The TagType corresponding to the edge device operational status and
 * vibration sensor value.
 */
export const vibrationSensorToTagType = (
  vibrationSensor: number | null,
  thresholds: EquipmentAlarmThresholds,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (
    _.isNil(vibrationSensor)
    || operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    return TagType.neutral;
  }

  if (!_.isNil(thresholds.highWarning) && vibrationSensor > thresholds.highWarning) {
    return TagType.warning;
  }

  return TagType.success;
};

/**
 * Converts the running status of the crusher to a localized text string.
 *
 * @param {CrusherRunningSStatus | null} [runningStatus] - The running status of the crusher.
 * @returns {string} - The localized text string corresponding to the crusher's running status.
 */
export const runningStatusToText = (
  runningStatus: CrusherRunningSStatus | null | undefined,
): string => {
  switch (runningStatus) {
    case CrusherRunningSStatus.running:
      return i18n.t('common.running');
    case CrusherRunningSStatus.starting:
      return i18n.t('common.starting');
    case CrusherRunningSStatus.fault:
      return i18n.t('common.fault');
    case CrusherRunningSStatus.off:
      return i18n.t('common.off');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the running status of the crusher and the edge device operational status to a TagStatus.
 *
 * @param {CrusherRunningStatus | null} runningStatus - The running status of the crusher.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status of
 * the edge device.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @param {CrusherRunningSStatus | null} runningStatus - The running status of the crusher.
 * @param operationalStatus - The operational status of the edge device.
 * device.
 * @returns {TagStatus} - The TagStatus corresponding to the [EquipmentStatusTag].
 */
export const runningStatusToEquipmentStatus = (
  runningStatus: CrusherRunningSStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(runningStatus)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagStatus.stopped;
  }

  switch (runningStatus) {
    case CrusherRunningSStatus.running:
      return TagStatus.running;
    case CrusherRunningSStatus.starting:
      return TagStatus.starting;
    case CrusherRunningSStatus.fault:
      return TagStatus.fault;
    case CrusherRunningSStatus.off:
      return TagStatus.stopped;
    default:
      return TagStatus.stopped;
  }
};

/**
 * Converts the edge device operational status and running status to a TagType.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * edge device.
 * @param {CrusherRunningSStatus | null} [runningStatus] - The running status of the crusher.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType corresponding to the edge device operational status and
 * running status.
 */
export const runningStatusToTagType = (
  runningStatus: CrusherRunningSStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(runningStatus)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (runningStatus) {
    case CrusherRunningSStatus.running:
      return TagType.success;
    case CrusherRunningSStatus.starting:
      return TagType.warning;
    case CrusherRunningSStatus.fault:
      return TagType.critical;
    case CrusherRunningSStatus.off:
      return TagType.neutral;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the edge device operational status and running status to a StatusIndicatorStatus.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - Edge Device status [on/off].
 * @param {CrusherRunningSStatus | null} [runningStatus] - The running status of the crusher.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {StatusIndicatorStatus} - StatusIndicatorStatus is the [status] of the StatusIndicator.
 */
export const runningStatusToIndicatorStatus = (
  runningStatus: CrusherRunningSStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(runningStatus)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  switch (runningStatus) {
    case CrusherRunningSStatus.running:
      return StatusIndicatorStatus.good;
    case CrusherRunningSStatus.starting:
      return StatusIndicatorStatus.warning;
    case CrusherRunningSStatus.fault:
      return StatusIndicatorStatus.alarm;
    case CrusherRunningSStatus.off:
      return StatusIndicatorStatus.neutral;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Converts the edge device operational status and running status to a StatusIndicatorStatus.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - Edge Device status [on/off].
 * @param {CrusherRunningEStatus | null} [runningStatus] - The running status of the crusher.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {StatusIndicatorStatus} - StatusIndicatorStatus is the [status] of the StatusIndicator.
 */
export const runningEnabledStatusToIndicatorStatus = (
  runningStatus: CrusherRunningEStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(runningStatus)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  switch (runningStatus) {
    case CrusherRunningEStatus.running:
      return StatusIndicatorStatus.good;
    case CrusherRunningEStatus.enabled:
      return StatusIndicatorStatus.warning;
    case CrusherRunningEStatus.fault:
      return StatusIndicatorStatus.alarm;
    case CrusherRunningEStatus.off:
      return StatusIndicatorStatus.neutral;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Converts the edge device operational status and running status to a TagType.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - Edge Device status [on/off].
 * @param {CrusherRunningEStatus | null} [runningStatus] - The running status of the crusher.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - TagType is the [type] of the Tag.
 */
export const runningEnabledStatusToTagType = (
  runningStatus: CrusherRunningEStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(runningStatus)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (runningStatus) {
    case CrusherRunningEStatus.running:
      return TagType.success;
    case CrusherRunningEStatus.enabled:
      return TagType.warning;
    case CrusherRunningEStatus.fault:
      return TagType.critical;
    case CrusherRunningEStatus.off:
      return TagType.neutral;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the edge device operational status and running status to a localized text string.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - Edge Device status [on/off].
 * @param {CrusherRunningEStatus | null} [runningStatus] - The running status of the crusher.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {string} - The localized text string corresponding to the crusher's running status.
 */
export const runningEnabledStatusToText = (
  runningStatus: CrusherRunningEStatus | null | undefined,
): string => {
  switch (runningStatus) {
    case CrusherRunningEStatus.running:
      return i18n.t('common.running');
    case CrusherRunningEStatus.enabled:
      return i18n.t('common.enabled');
    case CrusherRunningEStatus.fault:
      return i18n.t('common.fault');
    case CrusherRunningEStatus.off:
      return i18n.t('common.off');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the running status of the countershaft and the edge device operational status to
 * a StatusIndicatorStatus.
 *
 * @param {boolean | null} runningStatus - The running status of the countershaft.
 * @param {EdgeDeviceOperationalStatus | null} operationalStatus - The operational status of the
 * edge device.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {StatusIndicatorStatus} - The StatusIndicatorStatus corresponding to the
 * edge device operational status and running status.
 */
export const runningStatusToCountershaftIndicator = (
  runningStatus: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(runningStatus)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  return runningStatus ? StatusIndicatorStatus.good : StatusIndicatorStatus.alarm;
};

/**
 * Converts the weather mode status of the crusher to a localized text string.
 *
 * @param {CrusherWeatherModeStatus | null} [weatherMode] - The weather mode status of the crusher.
 * @returns {string} - The localized text string corresponding to the crusher's weather mode status.
 */
export const weatherModeStatusToText = (
  weatherMode: CrusherWeatherModeStatus | null | undefined,
): string => {
  switch (weatherMode) {
    case CrusherWeatherModeStatus.on:
      return i18n.t('common.enabled');
    case CrusherWeatherModeStatus.off:
      return i18n.t('common.disabled');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the edge device operational status and weather mode status to a TagType.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - Edge Device status [on/off].
 * @param {CrusherWeatherModeStatus | null} [weatherMode] - The weather mode status of the crusher.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType is the [type] of the Tag.
 */
export const weatherModeStatusToTagType = (
  weatherMode: CrusherWeatherModeStatus | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (weatherMode) {
    case CrusherWeatherModeStatus.on:
      return TagType.success;
    case CrusherWeatherModeStatus.off:
      return TagType.neutral;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the lube tank heater status to a TagType.
 *
 * @param {boolean | null} lubeTankHeater - The lube tank heater status.
 * @param {EdgeDeviceOperationalStatus} operationalStatus - Edge Device status [on/off].
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType is the [type] of the Tag.
 */
export const lubeTankHeaterStatusToTagType = (
  lubeTankHeater: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (lubeTankHeater) {
    case true:
      return TagType.success;
    case false:
      return TagType.neutral;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the control mode of the crusher motor to a localized text string.
 *
 * @param {CrusherMotorControl | null} [controlMode] - The control mode of the crusher motor.
 * @returns {string} - The localized text string corresponding to the crusher motor's control mode.
 */
export const controlModeToText = (
  controlMode: CrusherMotorControl | null | undefined,
): string => {
  switch (controlMode) {
    case CrusherMotorControl.auto:
      return i18n.t('common.auto');
    case CrusherMotorControl.manual:
      return i18n.t('common.manual');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the edge device operational status and control mode to a TagType.
 *
 * @param {EdgeDeviceOperationalStatus} operationalStatus - Edge Device status [on/off].
 * @param {CrusherMotorControl | null} [controlMode] - The control mode of the crusher motor.
 * @returns {TagType} - The TagType is the [type] of the Tag.
 */
export const controlModeToTagType = (
  controlMode: CrusherMotorControl | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || (isDeviceDisconnected(lastDataTimestamp))
    || _.isNil(controlMode)) {
    return TagType.neutral;
  }

  switch (controlMode) {
    case CrusherMotorControl.auto:
      return TagType.success;
    case CrusherMotorControl.manual:
      return TagType.neutral;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the feeder automation mode of the crusher to a localized text string.
 *
 * @param {boolean | null} [status] - The feeder automation mode of the crusher
 * @param operationalStatus - The status of the edge device.
 * @returns {string} - The localized text string corresponding to the crusher's feeder
 * automation mode.
 */
export const feederAutomationModeToTagType = (
  status: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(status)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }

  switch (status) {
    case true:
      return TagType.success;
    case false:
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the feeder automation mode of the crusher to a localized text string.
 *
 * @param {boolean | null} [status] - The feeder automation mode of the crusher
 * @returns {string} - The localized text string corresponding to the crusher's feeder
 * automation mode.
 */
export const feederAutomationModeToText = (
  status: boolean | null | undefined,
): string => {
  switch (status) {
    case true:
      return i18n.t('common.auto');
    case false:
      return i18n.t('common.manual');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the permissive status to a localized text string.
 * Permissive status is inverted (true = off, false = on).
 * @param {boolean | null} permissive - The permissive status.
 * @returns {string} - The localized text string corresponding to the permissive status.
 */
export const permissiveToOnOffText = (permissive: boolean | null | undefined): string => {
  switch (permissive) {
    case true:
      return i18n.t('common.off');
    case false:
      return i18n.t('common.on');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the enabled/disabled status to a localized text string.
 *
 * @param {boolean | null} text - The enabled/disabled status.
 * @returns {string} - The localized text string corresponding to the enabled/disabled status.
 */
export const enabledDisabledToText = (text: boolean | null | undefined): string => {
  switch (text) {
    case true:
      return i18n.t('common.enabled');
    case false:
      return i18n.t('common.disabled');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the status to a localized text string.
 * Status is global (true = on, false = off).
 * @param {boolean | null} status - The text to be converted.
 * @returns {string} - The localized text string corresponding to the status.
 */
export const booleanToOnOffText = (status: boolean | null | undefined): string => {
  switch (status) {
    case true:
      return i18n.t('common.on');
    case false:
      return i18n.t('common.off');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts VSI drive type to a localized text string.
 *
 * @param {CrusherVSIDriveType | string | null} driveType - The VSI drive type.
 * @returns {string} - The localized text string corresponding to the drive type.
 */
export const vsiDriveTypeToText = (
  driveType: CrusherVSIDriveType | string | null | undefined,
): string => {
  switch (driveType) {
    case CrusherVSIDriveType.beltDrive:
      return i18n.t('view.crusher.vsi.driveType.beltDrive');
    case CrusherVSIDriveType.gearDrive:
      return i18n.t('view.crusher.vsi.driveType.gearDrive');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the automation type to a localized text string.
 *
 * @param {CrusherAutomationType | null | undefined} automationType - The automation type.
 * @returns {string} - The text corresponding to the automation type.
 */
export const automationTypeToText = (
  automationType: CrusherAutomationType | null | undefined,
): string => {
  switch (automationType) {
    case CrusherAutomationType.disabled:
      return i18n.t('common.disabled');
    case CrusherAutomationType.powerControl:
      return i18n.t('common.powerControl');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts the automation type to a TagType.
 *
 * @param {CrusherAutomationType | null | undefined} automationType - The automation type.
 * @param {EdgeDeviceOperationalStatus | null} operationalStatus - The operational edge status of
 * the edge device.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @returns {TagType} - The TagType is the [type] of the Tag.
 */
export const automationTypeToTagType = (
  automationType: CrusherAutomationType | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(automationType)
    || isDeviceDisconnected(lastDataTimestamp)) {
    return TagType.neutral;
  }
  switch (automationType) {
    case CrusherAutomationType.disabled:
      return TagType.neutral;
    case CrusherAutomationType.powerControl:
      return TagType.success;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the permissive status and operational status of the edge device to a TagType.
 *
 * @param {boolean | null} permissive - The permissive status.
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The status of the edge device.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType is the [type] of the Tag.
 */
export const permissiveToOnOffTagType = (
  permissive: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (
    operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(permissive)
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    return TagType.neutral;
  }

  switch (permissive) {
    case true:
      return TagType.critical;
    case false:
      return TagType.success;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts the boolean status to a TagType (true = good, false = fault).
 *
 * @param {boolean | null} value - The boolean status.
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The status of the edge device.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @returns {TagType} - The TagType is the [type] of the Tag.
 */
export const booleanToTagType = (
  value: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (
    operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(value)
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    return TagType.neutral;
  }

  switch (value) {
    case true:
      return TagType.success;
    case false:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Function to check if the equipment is running.
 * @param operationalStatus - The status of the edge device.
 * @param lastDataTimestamp - The timestamp of the last received data.
 * @param threshold - The threshold to consider the PLC disconnected.
 * @returns {StatusIndicatorStatus} - The StatusIndicatorStatus is the [status]
 * of the StatusIndicator.
 */
export const isEquipmentRunning = (
  operationalStatus: EdgeDeviceOperationalStatus | null | undefined,
  lastDataTimestamp: number | null | undefined,
  threshold: number,
): boolean => (
  operationalStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on
  && !(lastDataTimestamp && (Date.now() - lastDataTimestamp) > threshold)
);

/**
  * Function to get the alert message based on the operational status and the last data timestamp.
  * @param {EdgeDeviceOperationalStatusStatus | null | undefined} operationalStatus
  * - The operational status of the crusher.
  * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
  * @param {string | null | undefined} readStatus - The read status of the data.
  * @param {number} threshold - The threshold to consider the Device disconnected.
  * @returns {string | null} - The alert message.
*/
export const getOperationalAlert = (
  operationalStatus: EdgeDeviceOperationalStatusStatus | null | undefined,
  lastDataTimestamp: number | null | undefined,
  readStatus: EquipmentDataReadStatus | undefined,
  threshold: number,
): string | null => {
  let text: string | null = null;

  if (operationalStatus === EdgeDeviceOperationalStatusStatus.off) {
    text = i18n.t('view.crusher.alert.deviceOffline');
  } else if (readStatus === EquipmentDataReadStatus.invalid) {
    text = i18n.t('view.crusher.alert.plcCommunicationError');
  } else if (
    lastDataTimestamp
    && Date.now() - lastDataTimestamp > threshold
    && readStatus === EquipmentDataReadStatus.valid
  ) {
    text = i18n.t('view.crusher.alert.communicationError');
  }

  return text;
};

/**
 * Extracts the GPS data from the latest unit data
 *
 * @param {CrusherOperationalData} data - The operational data of the crusher
 * @returns {GPS | null} - The GPS data
 */
export const extractGpsFromLatestUnitData = (
  data: CrusherOperationalData,
): GPS | null => {
  // Search for valid GPS data in the array (from newest to oldest)
  const itemWithGps = _.findLast(
    data,
    (item): boolean => {
      const hasGpsProperties = (
        'lat' in item
        && 'lng' in item
        && 'gpsTimestamp' in item
      );
      return hasGpsProperties && !_.isNil(item.lat) && !_.isNil(item.lng);
    },
  ) as (
    EquipmentUnitDataVSICrusher | EquipmentUnitDataConeCrusher
  ) | undefined;

  if (_.isNil(itemWithGps)) {
    return null;
  }

  return {
    lat: itemWithGps.lat,
    lng: itemWithGps.lng,
    gpsTimestamp: itemWithGps.gpsTimestamp,
  };
};

/**
 * Retrieves the last operational edge status from the provided operational data
 *
 * @param {CrusherOperationalData | null} operationalData - The operational data of the crusher
 * @returns {EdgeDeviceOperationalStatusStatus | null} - The last operational edge status
 */
export const lastOperationalEdgeStatus = (
  operationalData: CrusherOperationalData | null,
): EdgeDeviceOperationalStatus | null => _.findLast(
  operationalData,
  (
    item: (
      EquipmentUnitDataConeCrusher | EquipmentUnitDataVSICrusher
    ) | EdgeDeviceOperationalStatus,
  ): item is EdgeDeviceOperationalStatus => item && 'edgeStatus' in item,
) || null;

/**
 * Retrieves the last operational unit data from the provided operational data for Cone crusher
 *
 * @param {CrusherOperationalData | null} operationalData - The operational data of the crusher
 * @returns {EquipmentUnitDataConeCrusher | null} - The last operational unit data for Cone
 */
export const lastOperationalUnitDataCone = (
  operationalData: CrusherOperationalData | null,
): EquipmentUnitDataConeCrusher | null => _.findLast(
  operationalData,
  (
    item: (
      EquipmentUnitDataConeCrusher | EquipmentUnitDataVSICrusher
    ) | EdgeDeviceOperationalStatus,
  ): item is EquipmentUnitDataConeCrusher => item && !('edgeStatus' in item),
) || null;

/**
 * Retrieves the last operational unit data from the provided operational data for VSI crusher
 *
 * @param {CrusherOperationalData | null} operationalData - The operational data of the crusher
 * @returns {EquipmentUnitDataVSICrusher | null} - The last operational unit data for VSI
 */
export const lastOperationalUnitDataVSI = (
  operationalData: CrusherOperationalData | null,
): EquipmentUnitDataVSICrusher | null => _.findLast(
  operationalData,
  (
    item: (
      EquipmentUnitDataConeCrusher | EquipmentUnitDataVSICrusher
    ) | EdgeDeviceOperationalStatus,
  ): item is EquipmentUnitDataVSICrusher => item && !('edgeStatus' in item),
) || null;

/**
 * Function to format the crusher model
 *
 * @param {CrusherModel} model - The model of the crusher
 * @returns {string} - The formatted model of the crusher
 */
export const formatCrusherModel = (model: CrusherModel): string => {
  const modelPrefixName: Record<CrusherModel, string | null> = {
    [CrusherModel.p200]: 'Patriot®',
    [CrusherModel.p300]: 'Patriot®',
    [CrusherModel.p400]: 'Patriot®',
    [CrusherModel.p500]: 'Patriot®',
    [CrusherModel.p600]: 'Patriot®',
    [CrusherModel.d350]: 'Dakota®',
    [CrusherModel.vsi]: 'Valor®',
  };

  // Verify if the model has a prefix name
  const prefixName: string = modelPrefixName[model] ? `${modelPrefixName[model]} ` : '';

  // Return the formatted model
  return `${prefixName}${model.toUpperCase()}`;
};

/**
 * Check if the crusher model is Dakota (D350).
 *
 * @param {CrusherModel | null | undefined} model - The crusher model.
 * @returns {boolean} - True if the model is Dakota (D350), false otherwise.
 */
export const isDakotaModel = (model: CrusherModel | null | undefined): boolean => {
  if (_.isNil(model)) {
    return false;
  }

  return model === CrusherModel.d350;
};

/**
 * Determines if a setup component should be enabled.
 * Logic: Only true = enabled, everything else = disabled
 *
 * @param {boolean | null | undefined} enableValue - The enable value from setup data.
 * @returns {boolean} - Whether the component should be enabled.
 */
export const booleanToEnabled = (
  enableValue: boolean | null | undefined,
): boolean => enableValue === true;

/**
 * Determines if the crusher drive motor type is VFD.
 *
 * @param {CrusherDriveMotorType | null | undefined} crusherDriveMotorType
 * - The crusher drive motor type. (PLC)
 * @returns {boolean} - True if the crusher drive motor type is VFD, false otherwise.
 */
export const crusherDriveMotorTypeToEnabled = (
  crusherDriveMotorType: CrusherDriveMotorType | null | undefined,
): boolean => crusherDriveMotorType === CrusherDriveMotorType.vfd;

/**
 * Determines the status type for setup components based on connection and enable state.
 * Logic: If was enabled and lost connection → neutral, if was disabled → stays neutral
 *
 * @param {boolean | null | undefined} enableValue - The enable value from setup data.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status.
 * @param {number | null} lastDataTimestamp - The timestamp of the last data.
 * @returns {EnableItemStatus} - The status type for the component
 */
export const booleanToEnableStatus = (
  enableValue: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null,
): EnableItemStatus => {
  let status: EnableItemStatus = EnableItemStatus.neutral;

  if (
    operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(enableValue)
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    status = EnableItemStatus.neutral;
  } else if (enableValue === true) {
    status = EnableItemStatus.active;
  }

  return status;
};

/**
 * Determines the status type for setup components based on connection and enable state.
 * Logic: If was enabled and lost connection → neutral, if was disabled → stays neutral
 *
 * @param {CrusherDriveMotorType | null | undefined} crusherDriveMotorType
 * - The crusher drive motor type. (PLC)
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status.
 * @param {number | null} lastDataTimestamp - The timestamp of the last data.
 * @returns {EnableItemStatus} - The status type for the component
 */
export const crusherDriveMotorTypeToEnableStatus = (
  crusherDriveMotorType: CrusherDriveMotorType | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null,
): EnableItemStatus => {
  let status: EnableItemStatus = EnableItemStatus.neutral;

  if (
    operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(crusherDriveMotorType)
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    status = EnableItemStatus.neutral;
  } else if (crusherDriveMotorTypeToEnabled(crusherDriveMotorType)) {
    status = EnableItemStatus.active;
  }

  return status;
};

/**
 * Determines the quantity text for setup information cards.
 * Logic: Maps boolean values to quantity text (1 or 2)
 *
 * @param {boolean | null | undefined} quantityValue - The quantity value from setup data.
 * @returns {string} - The quantity text to display.
 */
export const booleanToQuantityText = (
  quantityValue: boolean | null | undefined,
): string => {
  switch (quantityValue) {
    case true:
      return '2';
    case false:
      return '1';
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Determines the status type for setup information cards based on connection.
 * Logic: If disconnected → neutral, if connected → active
 *
 * @param {boolean | null | undefined} quantityValue - The quantity value from setup data.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status.
 * @param {number | null} lastDataTimestamp - The timestamp of the last data.
 * @returns {QuantityStatus} - The status type for the quantity card.
 */
export const booleanToQuantityStatus = (
  quantityValue: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null,
): QuantityStatus => {
  let status: QuantityStatus = QuantityStatus.neutral;

  if (
    operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || _.isNil(quantityValue)
    || isDeviceDisconnected(lastDataTimestamp)
  ) {
    status = QuantityStatus.neutral;
  } else {
    status = QuantityStatus.active;
  }

  return status;
};

/**
 * Count active alarms from crusher data
 * @param crusherData - The crusher operational data
 * @returns The number of active alarms
 */
export const countActiveAlarms = (crusherData: EquipmentUnitDataConeCrusher | null): number => {
  let activeAlarmsCount: number = 0;

  if (crusherData) {
    // Count alarms that are in active status
    activeAlarmsCount = Object.keys(crusherData).reduce((count: number, key: string): number => {
      const alarmValue: string | number | boolean | null | undefined = crusherData[
        key as keyof EquipmentUnitDataConeCrusher
      ];
      return alarmValue === AlarmStatus.active ? count + 1 : count;
    }, 0);
  }

  return activeAlarmsCount;
};

/**
 * Maps a boolean value to a HighlightCard status.
 * FALSE = success (green)
 * TRUE = error (red)
 * null/undefined = neutral
 *
 * @param {boolean | null | undefined} value - The boolean value to map
 * @param {EdgeDeviceOperationalStatusStatus | null | undefined} edgeStatus - The current edge
 * status
 * @param {number | null | undefined} timestamp - The timestamp of the last data update
 * @returns {HightlightCardStatus} The mapped HighlightCard status
 */
export const numberToHighlightCardStatus = (
  value: number | null | undefined,
  edgeStatus: EdgeDeviceOperationalStatusStatus | null | undefined,
  timestamp: number | null | undefined,
): HightlightCardStatus => {
  let status = HightlightCardStatus.success;

  if (!edgeStatus || isDeviceDisconnected(timestamp) || _.isNil(value)) {
    status = HightlightCardStatus.neutral;
  } else if (value) {
    status = HightlightCardStatus.error;
  }

  return status;
};

/**
 * Converts the edge device operational status and permissive status to a StatusIndicatorStatus.
 * The logic is global (true = alarm, false = good).
 * @param {boolean | null} status - The status.
 * @param {EdgeDeviceOperationalStatus} operationalStatus - The operational status of the
 * edge device.
 * @param isDeviceDisconnected - The function to check if the Device is disconnected.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last received data.
 * @returns {StatusIndicatorStatus} - The StatusIndicatorStatus corresponding to the edge device
 * operational status and permissive status.
 */
export const booleanStatusToIndicatorStatus = (
  status: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  switch (status) {
    case true:
      return StatusIndicatorStatus.alarm;
    case false:
      return StatusIndicatorStatus.good;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Maps an IFM analog value to a ModuleIFMStatus based on its range.
 * Success (green) if between 4000 and 20000, Error (red) if outside this range.
 *
 * @param {number | null | undefined} value - The analog value to check.
 * @param {EdgeDeviceOperationalStatusStatus | null} edgeStatus - The edge device status.
 * @param {number | null | undefined} timestamp - The timestamp of the last data update.
 * @returns {ModuleIFMStatus} - Returns success if in range, error if out of range, neutral if null
 * or when edge is off.
 */
export const ifmAnalogValueToModuleStatus = (
  value: number | null | undefined,
  edgeStatus: EdgeDeviceOperationalStatusStatus | null,
  timestamp: number | null | undefined,
): ModuleIFMStatus => {
  let status: ModuleIFMStatus = ModuleIFMStatus.neutral;

  if (
    edgeStatus === EdgeDeviceOperationalStatusStatus.on
    && !isDeviceDisconnected(timestamp)
    && !_.isNil(value)
    && value >= 4000
    && value <= 20000
  ) {
    status = ModuleIFMStatus.success;
  }

  return status;
};

/**
 * Maps an IFM boolean value to a ModuleIFMStatus.
 * Success (green) if false, Error (red) if true.
 *
 * @param {boolean | null | undefined} value - The boolean value to convert.
 * @param {EdgeDeviceOperationalStatusStatus | null} edgeStatus - The edge device status.
 * @param {number | null | undefined} timestamp - The timestamp of the last data update.
 * @returns {ModuleIFMStatus} - Returns success for false, error for true, neutral for null
 * or when edge is off.
 */
export const ifmBooleanToModuleStatus = (
  value: boolean | null | undefined,
  edgeStatus: EdgeDeviceOperationalStatusStatus | null,
  timestamp: number | null | undefined,
): ModuleIFMStatus => {
  let status = ModuleIFMStatus.neutral;

  if (!edgeStatus || isDeviceDisconnected(timestamp) || _.isNil(value)) {
    status = ModuleIFMStatus.neutral;
  } else if (value) {
    status = ModuleIFMStatus.success;
  }

  return status;
};

/**
 * Converts a CrusherOperationMode to a ModuleIFMStatus.
 * Success (green) if local or remote, Error (red) if service.
 *
 * @param {CrusherOperationMode | null | undefined} mode - The operation mode to convert.
 * @param {EdgeDeviceOperationalStatusStatus | null} edgeStatus - The edge device status.
 * @param {number | null | undefined} timestamp - The timestamp of the last data update.
 * @returns {ModuleIFMStatus} - Returns success for local/remote, error for service, neutral for
 * null or when edge is off.
 */
export const operationModeToModuleStatus = (
  mode: CrusherOperationMode | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  timestamp: number | null | undefined,
): ModuleIFMStatus => {
  let status = ModuleIFMStatus.neutral;

  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(timestamp)
    || _.isNil(mode)) {
    status = ModuleIFMStatus.neutral;
  } else if (mode === CrusherOperationMode.service) {
    status = ModuleIFMStatus.success;
  } else {
    status = ModuleIFMStatus.neutral;
  }

  return status;
};

/**
 * Converts a PLC boolean value to a StatusIndicatorStatus.
 *
 * @param {boolean | null | undefined} value - The boolean value to convert.
 * @param {EdgeDeviceOperationalStatusStatus | null} edgeStatus - The edge device status.
 * @param {number | null | undefined} timestamp - The timestamp of the last data update.
 * @returns {StatusIndicatorStatus} - Returns good for true, neutral for false, neutral for null
 * or when edge is off.
 */
export const plcBooleanToStatus = (
  value: boolean | null | undefined,
  edgeStatus: EdgeDeviceOperationalStatusStatus | null,
  timestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (edgeStatus !== EdgeDeviceOperationalStatusStatus.on || isDeviceDisconnected(timestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  if (_.isNil(value)) {
    return StatusIndicatorStatus.neutral;
  }

  switch (value) {
    case true:
      return StatusIndicatorStatus.good;
    case false:
      return StatusIndicatorStatus.neutral;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Converts a CrusherOperationMode to a StatusIndicatorStatus.
 *
 * @param {CrusherOperationMode | null | undefined} mode - The operation mode to convert.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status.
 * @param {number | null | undefined} timestamp - The timestamp of the last data update.
 * @returns {StatusIndicatorStatus} - Returns good for remote, neutral for local/service, neutral
 * for null or when edge is off.
 */
export const plcToOperationMode = (
  mode: CrusherOperationMode | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  timestamp: number | null | undefined,
): StatusIndicatorStatus => {
  if (
    operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(timestamp)) {
    return StatusIndicatorStatus.neutral;
  }

  switch (mode) {
    case CrusherOperationMode.remote:
      return StatusIndicatorStatus.good;
    case CrusherOperationMode.local:
      return StatusIndicatorStatus.neutral;
    case CrusherOperationMode.service:
      return StatusIndicatorStatus.neutral;
    default:
      return StatusIndicatorStatus.neutral;
  }
};

/**
 * Converts a combo unit sensor boolean value to a TagType.
 *
 * @param {boolean | null | undefined} status - The boolean sensor status value.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last data update.
 * @returns {TagType} - Returns critical for true, success for false, neutral for null or when
 * edge is off.
 */
export const comboUnitSensorToTagType = (
  status: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): TagType => {
  if (
    operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)
    || _.isNil(status)) {
    return TagType.neutral;
  }

  switch (status) {
    case true:
      return TagType.success;
    case false:
      return TagType.critical;
    default:
      return TagType.neutral;
  }
};

/**
 * Converts a combo unit sensor boolean value to a localized text string.
 *
 * @param {boolean | null | undefined} status - The boolean sensor status value.
 * @returns {string} - Returns localized text for sensor status, or DASHBOARD_NULLISH_TEXT when
 * status is null or undefined.
 */
export const sensorValueToStatusText = (
  status: boolean | null | undefined,
): string => {
  switch (status) {
    case true:
      return i18n.t('view.crusher.maintenance.comboUnit.sensorStatus.statusOk');
    case false:
      return i18n.t('view.crusher.maintenance.comboUnit.sensorStatus.statusNotOk');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Converts a liner contact boolean value to a localized text string.
 *
 * @param {boolean | null | undefined} status - The boolean liner contact status value.
 * @returns {string} - Returns localized text for liner contact status,
 * or DASHBOARD_NULLISH
 */
export const linerContactToStatusText = (
  status: boolean | null | undefined,
): string => {
  switch (status) {
    case true:
      return i18n.t('view.crusher.maintenance.comboUnit.sensorStatus.contact');
    case false:
      return i18n.t('view.crusher.maintenance.comboUnit.sensorStatus.noContact');
    default:
      return DASHBOARD_NULLISH_TEXT;
  }
};

/**
 * Determines background and text colors based on sensor thresholds.
 * Universal logic: ALARM > WARNING > NORMAL (same as Speedometer)
 *
 * @param {number | null | undefined} value - The current sensor value.
 * @param {Object} thresholds - Object containing threshold values.
 * @param {number | null | undefined} thresholds.highAlarm - High alarm threshold.
 * @param {number | null | undefined} thresholds.highWarning - High warning threshold.
 * @param {number | null | undefined} thresholds.lowAlarm - Low alarm threshold.
 * @param {number | null | undefined} thresholds.lowWarning - Low warning threshold.
 * @param {Object} themeColors - Theme color object.
 * @param {boolean} running - Whether the equipment is running (default: true).
 * @returns {Object} Object containing backgroundColor and textColor.
 */
export const getSensorThresholdColors = (
  value: number | null | undefined,
  thresholds: Thresholds,
  themeColors: Theme['color']['crusherDashboard']['maintenance']['comboUnit'],
  running: boolean = true,
): {
  backgroundColor: string | undefined;
  textColor: string;
} => {
  let backgroundColor: string | undefined;
  let textColor: string;

  // Determines the current color based on thresholds (same logic as Speedometer)
  if (!running) {
    backgroundColor = themeColors.offBackgroundColor;
    textColor = themeColors.offTextColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(thresholds.highAlarm) && value >= thresholds.highAlarm)
      || (!_.isNil(thresholds.lowAlarm) && value <= thresholds.lowAlarm)
    )
  ) {
    backgroundColor = themeColors.alarmBackgroundColor;
    textColor = themeColors.criticalTypeTextColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(thresholds.highWarning) && value >= thresholds.highWarning)
      || (!_.isNil(thresholds.lowWarning) && value <= thresholds.lowWarning)
    )
  ) {
    backgroundColor = themeColors.warningBackgroundColor;
    textColor = themeColors.warningTypeTextColor;
  } else {
    // NORMAL: value within limits
    backgroundColor = themeColors.normalBackgroundColor;
    textColor = themeColors.normalTextColor;
  }

  return { backgroundColor, textColor };
};

/**
 * Converts a PLC boolean value to a SolenoidStatus.
 *
 * @param {boolean | null | undefined} status - The boolean value to convert.
 * @param {EdgeDeviceOperationalStatusStatus | null} operationalStatus - The operational status.
 * @param {number | null | undefined} lastDataTimestamp - The timestamp of the last data update.
 * @returns {SolenoidStatus} - Returns success for true, fault for false, disconnected for null
 * or when edge is off.
 */
export const booleanToSolenoidStatus = (
  status: boolean | null | undefined,
  operationalStatus: EdgeDeviceOperationalStatusStatus | null,
  lastDataTimestamp: number | null | undefined,
): SolenoidStatus => {
  if (operationalStatus !== EdgeDeviceOperationalStatusStatus.on
    || isDeviceDisconnected(lastDataTimestamp)) {
    return SolenoidStatus.disconnected;
  }

  switch (status) {
    case true:
      return SolenoidStatus.success;
    case false:
      return SolenoidStatus.fault;
    default:
      return SolenoidStatus.disabled;
  }
};

/**
 * Checks if a feature, module, or sensor is enabled.
 * Considers null/undefined as enabled (default behavior).
 * Only explicitly false values are considered disabled.
 *
 * @param {boolean | null | undefined} enabled - The enabled status value.
 * @returns {boolean} - True if the feature should be visible/enabled.
 */
export const isEnabled = (enabled: boolean | null | undefined): boolean => enabled !== false;

/**
* Gets the schema type for the given equipment
* @param equipment - Equipment unit to analyze
* @returns Schema type: 'patriot', 'vsi'
*/
export const getSchemaType = (equipment: EquipmentUnitCrusher | null): SchemaType | undefined => {
  let schemaType: SchemaType | undefined;

  if (
    equipment
    && equipment.dataSchema === CrusherDataSchema.d1vsi
    && equipment.plcTagSchema === CrusherPLCTagSchema.t1vsi
  ) {
    schemaType = SchemaType.vsi;
  } else if (
    equipment
    && equipment.dataSchema === CrusherDataSchema.d1patriot
    && equipment.plcTagSchema === CrusherPLCTagSchema.t1patriot
  ) {
    schemaType = SchemaType.patriot;
  }

  return schemaType;
};
